import { useEffect, useState } from "react";
import { Button, Form } from "react-bootstrap";
import moment, { Moment } from "moment";
import { getOneRegistry } from "service/gatewayService";
import downloadIcon from "assets/svg/download.svg";
import { walletModalTrigger } from "hooks/wallet";
import { HoverAuthorizeTooltip } from "components/authorize/AuthorizeTooltip";
import ContentWrapper from "components/content-wrapper/ContentWrapper";
import { HttpStatus } from "constant";
import TimezoneDropdown from "components/common/TimezoneDropdown";
import { useOrganisation } from "hooks/organization";
import DateTimeRangePicker from "components/common/DateTimeRangePicker";
import { BasicPlanModal } from "../modals/BasicPlanModal";
import { useBasicPlanModal } from "hooks/useBasicPlanModal";
import { useCheckIfBasicPlan } from "hooks/useCheckIfBasicPlan";
import { ConfirmDownloadModal } from "components/modals/ConfirmDownloadModal";
import { DownloadOptions, DownloadPayload } from "types";
import { DownloadOption } from "./DownloadOption";

const { OK } = HttpStatus;

export const DownloadDataUI = ({
    registryDetails,
    gatewayId,
    ldsuId,
    said,
    downloadType,
    disabled,
}: any) => {
    const { defaultTimeZone } = useOrganisation();
    const [startDateTime, setStartDateTime] = useState(
        moment().tz(defaultTimeZone)
    );
    const [endDateTime, setEndDateTime] = useState(
        moment().tz(defaultTimeZone)
    );
    const [minDate, setMinDate] = useState(
        moment.utc("1970-01-01").tz(defaultTimeZone)
    );
    const [onboardTime, setOnboardTime] = useState(0);
    const [downloadRangeType, setDownloadRangeType] = useState("downloadAll");

    const [modalShow, setModalShow] = useState(false);
    const DOWNLOAD_BY_DATE = "downloadByDate";
    const [error, setError] = useState(false);
    const [timezone, setTimezone] = useState<string>(defaultTimeZone);
    const { IS_BASIC_PLAN } = useCheckIfBasicPlan();
    const { toggleShowBasicPlanModal, showBasicPlanModal }: any =
        useBasicPlanModal();
    const [isDisableFastDownload, setIsDisableFastDownload] = useState(true);
    const [downloadOption, setDownloadOption] =
        useState<DownloadOptions>("email");

    useEffect(() => {
        if (registryDetails) {
            const gatewayOnboardTime = moment(
                registryDetails.create_time
            ).unix();
            setOnboardTime(gatewayOnboardTime);
            const twentyFourHoursAgo = moment().subtract(24, "hours").unix();

            if (gatewayOnboardTime > twentyFourHoursAgo) {
                setStartDateTime(moment(registryDetails.create_time));
            } else {
                setStartDateTime(
                    moment().subtract(24, "hours").tz(defaultTimeZone)
                );
            }
        } else {
            const fetch = async () => {
                const response: any = await getOneRegistry(gatewayId);
                const { data: registry, status } = response;
                if (status === OK) {
                    const gatewayOnboardTime = moment(
                        registry.create_time
                    ).unix();
                    setOnboardTime(gatewayOnboardTime);
                    const twentyFourHoursAgo = moment()
                        .subtract(24, "hours")
                        .unix();

                    if (gatewayOnboardTime > twentyFourHoursAgo) {
                        setStartDateTime(moment(registry.create_time));
                    } else {
                        setStartDateTime(
                            moment().subtract(24, "hours").tz(defaultTimeZone)
                        );
                    }
                }
            };
            fetch();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [gatewayId, registryDetails]);

    useEffect(() => {
        setTimezone(defaultTimeZone);
        setMinDate(minDate.tz(timezone));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [defaultTimeZone]);

    const updateOption = (e: any) => {
        const {
            target: { value },
        } = e;
        setDownloadRangeType(value);
        if (value === DOWNLOAD_BY_DATE) {
            setStartDateTime(startDateTime.tz(timezone));
            setEndDateTime(endDateTime.tz(timezone));
            setMinDate(minDate.tz(timezone));
        } else {
            setIsDisableFastDownload(true);
            setDownloadOption("email");
        }
    };

    const handleConfirmDownload = async (
        processDownload: (data: DownloadPayload) => any
    ) => {
        // Validate that dates are not empty when "Pick date range" is selected
        if (downloadRangeType === DOWNLOAD_BY_DATE) {
            if (!startDateTime || !endDateTime || !startDateTime.isValid() || !endDateTime.isValid()) {
                setError(true);
                return;
            }
        }
        
        await processDownload({
            start:
                downloadRangeType === DOWNLOAD_BY_DATE
                    ? moment(startDateTime).seconds(0).unix()
                    : moment(onboardTime).tz(timezone).unix(),
            end:
                downloadRangeType === DOWNLOAD_BY_DATE
                    ? moment(endDateTime).seconds(0).unix()
                    : moment().tz(timezone).unix(),
            attributes: [
                {
                    gateway_id:
                        downloadType === "downloadGateway"
                            ? registryDetails.gateway_id
                            : gatewayId,
                    ...(downloadType === "downloadLDSU" ||
                    downloadType === "downloadSensor"
                        ? { device_id: ldsuId }
                        : {}),
                    ...(downloadType === "downloadSensor"
                        ? { said: Number(said) }
                        : {}),
                },
            ],
            time_zone: timezone,
        });
    };

    const handleDateTimeRangeChange = (value: Moment[]) => {
        setStartDateTime(value[0]);
        setEndDateTime(value[1]);
        const disableFastDownload = value[1].diff(value[0], "minute") > 24 * 60;
        setIsDisableFastDownload(disableFastDownload);

        if (disableFastDownload) {
            setDownloadOption("email");
        }
    };

    const handleDateTimeRangeError = (error: string) => {
        setError(!!error);
    };

    const handleChangeTimeZone = (timezone: string) => {
        setTimezone(timezone);
    };

    const onChangeDownloadOption = (option: DownloadOptions) => {
        setDownloadOption(option);
    };

    return (
        <ContentWrapper>
            <div className="mt-3">
                <Form.Label>Select a time zone</Form.Label>
                <TimezoneDropdown
                    value={timezone}
                    onChange={handleChangeTimeZone}
                    className="dark"
                    disabled={disabled}
                />
            </div>
            <Form.Label className="mt-4">Select data to include</Form.Label>
            <HoverAuthorizeTooltip permission="gateway:update">
                <div className="download-data-option ">
                    <Form.Check
                        name="download-data"
                        custom
                        type="radio"
                        value="downloadAll"
                        id={"download-all"}
                        checked={downloadRangeType === "downloadAll"}
                        onChange={(e: any) => {
                            updateOption(e);
                        }}
                        label={<span className="ml-2">Download all data</span>}
                        disabled={disabled}
                    />
                </div>
            </HoverAuthorizeTooltip>
            <HoverAuthorizeTooltip permission="gateway:update">
                <div className="download-data-option">
                    <Form.Check
                        name="download-data"
                        custom
                        type="radio"
                        value={DOWNLOAD_BY_DATE}
                        id={"pick-date"}
                        checked={downloadRangeType === DOWNLOAD_BY_DATE}
                        onChange={(e: any) => {
                            updateOption(e);
                        }}
                        label={<span className="ml-2">Pick date range</span>}
                        disabled={disabled}
                    />
                    {downloadRangeType === DOWNLOAD_BY_DATE ? (
                        <div className="mt-4 mb-4">
                            <DateTimeRangePicker
                                value={[startDateTime, endDateTime]}
                                timezone={timezone}
                                onChange={handleDateTimeRangeChange}
                                onError={handleDateTimeRangeError}
                            />
                        </div>
                    ) : null}
                </div>
            </HoverAuthorizeTooltip>
            <HoverAuthorizeTooltip permission="gateway:update">
                <div className="mt-3">
                    <DownloadOption
                        selectedOption={downloadOption}
                        onChange={onChangeDownloadOption}
                        fastDisabled={isDisableFastDownload}
                        variant="dark"
                    />
                </div>
            </HoverAuthorizeTooltip>
            <HoverAuthorizeTooltip
                permission="gateway:update"
                className="w-100"
            >
                <Button
                    disabled={
                        disabled ||
                        (error && downloadRangeType === DOWNLOAD_BY_DATE)
                    }
                    className="w-100 mt-3"
                    onClick={() => {
                        if (IS_BASIC_PLAN) {
                            toggleShowBasicPlanModal();
                        } else {
                            walletModalTrigger(() => {
                                setModalShow(true);
                            });
                        }
                    }}
                >
                    <img src={downloadIcon} alt="" />
                    Download Data
                </Button>
            </HoverAuthorizeTooltip>
            <ConfirmDownloadModal
                show={modalShow}
                onClose={() => setModalShow(false)}
                onConfirm={handleConfirmDownload}
                downloadType={downloadOption}
            />
            <BasicPlanModal
                showBasicPlanModal={showBasicPlanModal}
                toggleShowBasicPlanModal={toggleShowBasicPlanModal}
            />
        </ContentWrapper>
    );
};
